import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { devTasksAPI } from '@/lib/api';
import { toast } from 'sonner';
import { BrutalCard, BrutalButton } from '@/components/brutal';

const SEV = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-300 text-black',
  low: 'bg-gray-200 text-black',
};
const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', fixed: 'Fixed', wont_fix: "Won't fix" };
const inputCls = 'border-2 border-black px-3 py-2 text-sm w-full';
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const fmtMins = (m) => {
  if (m === null || m === undefined) return 'not recorded';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60); const r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
};
const SRC_LABEL = { logged: 'logged', estimated: 'estimated', elapsed: 'start to finish' };

const TaskRow = ({ t, onChange }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(t.notes || '');
  const [mins, setMins] = useState('');
  const closed = t.status === 'fixed' || t.status === 'wont_fix';
  const setStatus = async (status) => {
    try {
      const body = { status };
      if (status === 'fixed' && mins !== '') body.time_spent_minutes = parseInt(mins, 10);
      await devTasksAPI.update(t.id, body);
      toast.success(`Marked ${STATUS_LABEL[status].toLowerCase()}`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
  };
  const saveNotes = async () => {
    try { await devTasksAPI.update(t.id, { notes }); toast.success('Notes saved'); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };
  return (
    <div className={`border-2 border-black ${closed ? 'opacity-60' : ''}`} data-testid="devtask-row">
      <button type="button" className="w-full text-left p-3 flex flex-wrap gap-2 items-center" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className={`px-2 py-0.5 text-xs font-black uppercase ${SEV[t.severity]}`}>{t.severity}</span>
        <span className="font-bold flex-1 min-w-[12rem]">{t.title}</span>
        <span className="text-xs uppercase font-bold">{STATUS_LABEL[t.status]}</span>
      </button>
      {open && (
        <div className="p-3 border-t-2 border-black space-y-3 text-sm">
          {t.description && <p>{t.description}</p>}
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
            {t.area && (<><dt className="font-bold">Area</dt><dd>{t.area}</dd></>)}
            {t.evidence && (<><dt className="font-bold">Where</dt><dd className="font-mono text-xs break-all">{t.evidence}</dd></>)}
            <dt className="font-bold">Found</dt><dd>{fmtTime(t.found_date)} in version {t.found_in_version || 'unknown'}{t.source ? ` (${t.source})` : ''}</dd>
            {t.started_date && (<><dt className="font-bold">Started</dt><dd>{fmtTime(t.started_date)}</dd></>)}
            {closed && (<><dt className="font-bold">Completed</dt><dd>{fmtTime(t.fixed_date)} in version {t.fixed_in_version || 'unknown'}</dd></>)}
            {(closed || t.time_spent_minutes != null) && (<><dt className="font-bold">Time spent</dt><dd>{fmtMins(t.time_spent_minutes)}{t.time_source ? ` (${SRC_LABEL[t.time_source] || t.time_source})` : ''}</dd></>)}
          </dl>
          <label className="block text-xs font-bold uppercase">Notes
            <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          {!closed && (
            <label className="block text-xs font-bold uppercase">Time spent (minutes), saved when you mark it fixed
              <input type="number" min="0" className={inputCls} value={mins} onChange={(e) => setMins(e.target.value)} placeholder="Leave empty to use start-to-finish time" />
            </label>
          )}
          <div className="flex flex-wrap gap-2">
            <BrutalButton onClick={saveNotes}>Save notes</BrutalButton>
            {t.status !== 'in_progress' && !closed && <BrutalButton onClick={() => setStatus('in_progress')}>Start</BrutalButton>}
            {!closed && <BrutalButton variant="amber" onClick={() => setStatus('fixed')}>Mark fixed in this version</BrutalButton>}
            {!closed && <BrutalButton onClick={() => setStatus('wont_fix')}>Won't fix</BrutalButton>}
            {closed && <BrutalButton onClick={() => setStatus('open')}>Reopen</BrutalButton>}
          </div>
        </div>
      )}
    </div>
  );
};

const DevTasksTab = () => {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('open');
  const [view, setView] = useState('tasks');
  const [form, setForm] = useState({ title: '', severity: 'medium', area: '', description: '', evidence: '' });
  const [change, setChange] = useState({ title: '', details: '', kind: 'fix' });

  const tasks = useQuery({ queryKey: ['dev-tasks'], queryFn: async () => (await devTasksAPI.list()).data });
  const log = useQuery({ queryKey: ['dev-changelog'], queryFn: async () => (await devTasksAPI.changelog()).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['dev-tasks'] }); qc.invalidateQueries({ queryKey: ['dev-changelog'] }); };

  const data = tasks.data;
  const shown = (data?.tasks || []).filter((t) => {
    const closed = t.status === 'fixed' || t.status === 'wont_fix';
    return filter === 'all' ? true : filter === 'open' ? !closed : closed;
  });

  const addTask = async (e) => {
    e.preventDefault();
    try { await devTasksAPI.create(form); toast.success('Task added'); setForm({ title: '', severity: 'medium', area: '', description: '', evidence: '' }); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not add the task'); }
  };
  const addChange = async (e) => {
    e.preventDefault();
    try { await devTasksAPI.addChange(change); toast.success('Recorded'); setChange({ title: '', details: '', kind: 'fix' }); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not record the change'); }
  };

  return (
    <div className="space-y-6" data-testid="devtasks-tab">
      <BrutalCard className="p-5">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div>
            <h2 className="text-2xl font-black uppercase">Fix list and change log</h2>
            <p className="text-sm">Running version: <span className="font-mono font-bold">{data?.version?.short || '…'}</span>. Closing a task stamps today's date and this version.</p>
          </div>
          {data && (
            <div className="flex flex-wrap gap-2 text-xs font-black uppercase">
              {['critical', 'high', 'medium', 'low'].map((s) => (
                <span key={s} className={`px-2 py-1 ${SEV[s]}`}>{data.open_by_severity?.[s] ?? 0} {s}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-4">
          <BrutalButton variant={view === 'tasks' ? 'amber' : undefined} onClick={() => setView('tasks')}>Fix list</BrutalButton>
          <BrutalButton variant={view === 'log' ? 'amber' : undefined} onClick={() => setView('log')}>Change log</BrutalButton>
        </div>
      </BrutalCard>

      {view === 'tasks' && (
        <>
          <div className="flex gap-2">
            {[['open', 'Open'], ['closed', 'Closed'], ['all', 'All']].map(([id, label]) => (
              <BrutalButton key={id} variant={filter === id ? 'amber' : undefined} onClick={() => setFilter(id)}>{label}</BrutalButton>
            ))}
          </div>
          {tasks.isLoading ? <p>Loading…</p> : shown.length === 0 ? <p className="text-sm">Nothing here.</p> : (
            <div className="space-y-2">{shown.map((t) => <TaskRow key={t.id} t={t} onChange={refresh} />)}</div>
          )}
          <BrutalCard className="p-5">
            <h3 className="text-xl font-black uppercase mb-3">Add a task</h3>
            <form onSubmit={addTask} className="grid md:grid-cols-2 gap-3">
              <label className="text-xs font-bold uppercase md:col-span-2">Title<input required maxLength={200} className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
              <label className="text-xs font-bold uppercase">Severity
                <select className={inputCls} value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                  {['critical', 'high', 'medium', 'low'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="text-xs font-bold uppercase">Area<input className={inputCls} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></label>
              <label className="text-xs font-bold uppercase md:col-span-2">Where (file:line)<input className={inputCls} value={form.evidence} onChange={(e) => setForm({ ...form, evidence: e.target.value })} /></label>
              <label className="text-xs font-bold uppercase md:col-span-2">Details<textarea rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
              <div><BrutalButton variant="amber" type="submit">Add task</BrutalButton></div>
            </form>
          </BrutalCard>
        </>
      )}

      {view === 'log' && (
        <>
          <div className="space-y-2">
            {(log.data?.entries || []).length === 0 && <p className="text-sm">No entries yet.</p>}
            {(log.data?.entries || []).map((c) => (
              <BrutalCard key={c.id} className="p-4">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-xs font-black uppercase px-2 py-0.5 bg-black text-white">{c.kind}</span>
                  <span className="font-bold">{c.title}</span>
                  <span className="text-xs ml-auto">{fmt(c.date)} · version <span className="font-mono">{c.version}</span></span>
                </div>
                {c.details && <p className="text-sm mt-2">{c.details}</p>}
              </BrutalCard>
            ))}
          </div>
          <BrutalCard className="p-5">
            <h3 className="text-xl font-black uppercase mb-3">Record a change</h3>
            <form onSubmit={addChange} className="grid md:grid-cols-2 gap-3">
              <label className="text-xs font-bold uppercase md:col-span-2">What changed<input required maxLength={200} className={inputCls} value={change.title} onChange={(e) => setChange({ ...change, title: e.target.value })} /></label>
              <label className="text-xs font-bold uppercase">Kind
                <select className={inputCls} value={change.kind} onChange={(e) => setChange({ ...change, kind: e.target.value })}>
                  {['fix', 'security', 'feature', 'maintenance'].map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </label>
              <label className="text-xs font-bold uppercase md:col-span-2">Details<textarea rows={3} className={inputCls} value={change.details} onChange={(e) => setChange({ ...change, details: e.target.value })} /></label>
              <div><BrutalButton variant="amber" type="submit">Record</BrutalButton></div>
            </form>
          </BrutalCard>
        </>
      )}
    </div>
  );
};

export default DevTasksTab;
